import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  FaFileAlt,
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaTimes,
  FaSearch,
  FaSpinner,
  FaBuilding,
  FaCheckCircle,
  FaClock,
  FaCheck,
  FaUser,
  FaCalendarAlt,
  FaPrint,
} from "react-icons/fa";
import apiRequest from "../services/api";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import DOMPurify from "dompurify";

const LetterManagement = () => {
  const [letters, setLetters] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // create | edit | view
  const [selectedLetter, setSelectedLetter] = useState(null);

  const [formData, setFormData] = useState({
    id: null,
    letterCode: "",
    title: "",
    body: "",
    date: "",
    status: "DRAFT",
    approverId: "",
    approverName: "",
  });

  const fetchLetters = useCallback(async () => {
    setIsLoading(true);
    setError("");
    try {
      const resp = await apiRequest("officeAdmin");
      // Support multiple response shapes:
      // 1) { data: LetterDto[] }
      // 2) { data: { content: LetterDto[] } }
      // 3) LetterDto[] (if apiRequest already unwraps)
      const raw = resp?.data !== undefined ? resp.data : resp;
      const data = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.content)
        ? raw.content
        : [];
      setLetters(data);
    } catch (err) {
      setError(err?.message || "Unable to load letters.");
      setLetters([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLetters();
  }, [fetchLetters]);

  const [sortConfig, setSortConfig] = useState({
    key: "date",
    direction: "desc",
  });

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortedLetters = useMemo(() => {
    if (!Array.isArray(letters) || !letters.length) return [];

    return [...letters].sort((a, b) => {
      let aValue = a?.[sortConfig.key];
      let bValue = b?.[sortConfig.key];

      if (sortConfig.key === "date") {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      } else if (sortConfig.key === "approverName") {
        aValue = (a?.approverName || a?.approver?.fullName || "").toLowerCase();
        bValue = (b?.approverName || b?.approver?.fullName || "").toLowerCase();
      } else if (typeof aValue === "string" || typeof bValue === "string") {
        aValue = String(aValue ?? "").toLowerCase();
        bValue = String(bValue ?? "").toLowerCase();
      } else {
        aValue = aValue ?? "";
        bValue = bValue ?? "";
      }

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [letters, sortConfig]);

  const filteredLetters = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return sortedLetters.filter((letter) => {
      if (!letter) return false;
      const status = String(letter.status || "").toUpperCase();

      const matchesKeyword =
        !kw ||
        (letter.letterCode || "").toLowerCase().includes(kw) ||
        (letter.title || "").toLowerCase().includes(kw) ||
        (letter.body || "").toLowerCase().includes(kw) ||
        (letter.approverName || "").toLowerCase().includes(kw);

      return matchesKeyword && (!statusFilter || status === statusFilter);
    });
  }, [sortedLetters, keyword, statusFilter]);

  const filterOptions = useMemo(
    () => ({
      statuses: [
        ...new Set(
          (letters || [])
            .map((l) => String(l?.status || "").toUpperCase())
            .filter(Boolean)
        ),
      ].sort(),
    }),
    [letters]
  );

  const clearFilters = () => {
    setStatusFilter("");
    setKeyword("");
  };

  const SortableHeader = ({ label, sortKey, className = "" }) => {
    const isActive = sortConfig.key === sortKey;
    const direction = isActive ? sortConfig.direction : null;

    return (
      <th
        className={`px-3 lg:px-4 py-4 cursor-pointer select-none hover:text-slate-800 dark:hover:text-white transition ${className}`}
        onClick={() => handleSort(sortKey)}
      >
        <div
          className={`flex items-center gap-1.5 ${
            className.includes("text-right")
              ? "justify-end"
              : className.includes("text-center")
              ? "justify-center"
              : ""
          }`}
        >
          <span>{label}</span>
          <span className="text-[10px] opacity-70">
            {direction === "asc" ? "↑" : direction === "desc" ? "↓" : "↕"}
          </span>
        </div>
      </th>
    );
  };

  const openModal = async (mode, letter = null) => {
    setModalMode(mode);
    setSelectedLetter(letter);
    setError("");

    if (letter) {
      let formattedDate = "";
      if (letter.date) {
        formattedDate = String(letter.date).slice(0, 10);
      }

      setFormData({
        id: letter.id,
        letterCode: letter.letterCode || "",
        title: letter.title || "",
        body: letter.body || "",
        date: formattedDate,
        status: letter.status || "DRAFT",
        approverId: letter.approverId || letter.approver?.id || "",
        approverName: letter.approverName || letter.approver?.fullName || "",
      });
    } else {
      const today = new Date().toISOString().slice(0, 10);
      setFormData({
        id: null,
        letterCode: "",
        title: "",
        body: "",
        date: today,
        status: "DRAFT",
        approverId: "",
        approverName: "",
      });
    }

    setIsModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.body.trim()) {
      setError("Letter body is required.");
      return;
    }
    if (!formData.date) {
      setError("Letter date is required.");
      return;
    }

    const endpoint = "officeAdmin";
    const method = modalMode === "create" ? "POST" : "PUT";

    const payload = {
      id: formData.id,
      title: formData.title.trim(),
      body: formData.body.trim(),
      date: formData.date,
      status: formData.status,
    };

    try {
      await apiRequest(endpoint, {
        method,
        body: payload,
      });
      await fetchLetters();
      setIsModalOpen(false);
    } catch (err) {
      setError(err?.message || "Unable to save letter.");
    }
  };

  const handleApprove = async (id) => {
    if (!window.confirm("Approve this letter?")) return;
    try {
      await apiRequest(`officeAdmin/${id}/approve`, { method: "PUT" });
      await fetchLetters();
    } catch (err) {
      setError(err?.message || "Unable to approve letter.");
    }
  };

  const handleDelete = async (id, code) => {
    if (!window.confirm(`Delete letter "${code || id}"?`)) return;
    try {
      await apiRequest(`officeAdmin/${id}`, { method: "DELETE" });
      await fetchLetters();
    } catch (err) {
      setError(err?.message || "Unable to delete letter.");
    }
  };

  const handlePrint = (letter) => {
    if (!letter) return;

    const safe = (s) =>
      String(s ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

    const tenantName = safe(letter.tenantName || "Organization");
    const tenantPhone = safe(letter.tenantPhone || "");
    const tenantEmail = safe(letter.tenantEmail || "");
    const tenantAddress = safe(letter.tenantAddress || "");
    const logoUrl = letter.logoUrl || "";
    const refNo = safe(letter.letterCode || "");
    const letterDate = letter.date
      ? new Date(letter.date).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : "";
    const title = safe(letter.title || "");
    const rawBody = String(letter.body || "");

    const body = /<[a-z][\s\S]*>/i.test(rawBody)
      ? DOMPurify.sanitize(rawBody)
      : safe(rawBody).replace(/\r?\n/g, "<br>");

    const approver = safe(letter.approverName || "");

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${refNo || "Letter"} – ${title}</title>
  <style>
    @page { size: A4; margin: 0; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: "Segoe UI", Arial, sans-serif;
      color: #1e293b;
      background: #fff;
      width: 210mm;
      min-height: 297mm;
      margin: 0 auto;
      position: relative;
      display: flex;
      flex-direction: column;
    }
    .page-header { position: relative; padding: 28px 40px 0 40px; }
    .header-top-curve {
      position: absolute; top: 0; right: 0;
      width: 120px; height: 48px; background: #0088ce;
      border-radius: 0 0 0 80px;
    }
    .header-row { display: flex; align-items: center; gap: 16px; padding-bottom: 12px; }
    .logo-wrap {
      flex-shrink: 0; width: 64px; height: 64px; border-radius: 50%;
      overflow: hidden; background: #0088ce;
      display: flex; align-items: center; justify-content: center;
    }
    .logo-wrap img { width: 100%; height: 100%; object-fit: cover; }
    .logo-fallback { color: #fff; font-size: 28px; font-weight: 700; letter-spacing: -1px; }
    .company-names { flex: 1; min-width: 0; }
    .company-english {
      font-size: 18px; font-weight: 700; color: #0088ce; line-height: 1.3;
    }
    .header-line { height: 3px; background: #0088ce; margin-top: 4px; }
    .header-line-thin { height: 1px; background: #0088ce; margin-top: 3px; opacity: 0.5; }
    .ref-date-block { display: flex; justify-content: flex-end; padding: 18px 40px 0 40px; }
    .ref-date-inner { text-align: left; font-size: 13px; color: #1e293b; line-height: 1.8; }
    .ref-date-inner .label-am {
      font-family: "Nyala", "Abyssinica SIL", "Ethiopia Jiret", "Segoe UI", sans-serif;
      font-size: 13px;
    }
    .ref-date-inner .value {
      display: inline-block; min-width: 140px; border-bottom: 1px solid #94a3b8;
      margin-left: 6px; padding: 0 4px 1px 4px; font-weight: 600; color: #0f172a;
    }
    .letter-content { flex: 1; padding: 28px 40px 24px 40px; }
    .letter-title {
      font-size: 16px; font-weight: 700; color: #0f172a; margin-bottom: 16px;
      text-decoration: underline; text-underline-offset: 3px;
    }
    .letter-body {
      line-height: 1.75;
      font-size: 14px;
      color: #1e293b;
      text-align: justify;
      overflow-wrap: anywhere;
    }
    
    .letter-body p {
      margin: 0 0 10px;
    }
    
    .letter-body h1,
    .letter-body h2,
    .letter-body h3 {
      margin: 12px 0 8px;
      font-weight: 700;
    }
    
    .letter-body ul,
    .letter-body ol {
      margin: 8px 0;
      padding-left: 28px;
    }
    
    .letter-body ul {
      list-style-type: disc;
    }
    
    .letter-body ol {
      list-style-type: decimal;
    }
    
    .letter-body blockquote {
      margin: 10px 0;
      padding-left: 12px;
      border-left: 3px solid #94a3b8;
    }
    
    .letter-body a {
      color: #0369a1;
      text-decoration: underline;
    }
    .letter-sign { margin-top: 48px; font-size: 13px; color: #334155; }
    .letter-sign .approver-name {
      font-weight: 700; margin-top: 28px; font-size: 14px; color: #0f172a;
    }
    .page-footer { margin-top: auto; padding: 0 40px 20px 40px; position: relative; }
    .footer-line-row { display: flex; align-items: flex-end; margin-bottom: 12px; }
    .footer-line { flex: 1; height: 1.5px; background: #94a3b8; }
    .footer-curve {
      width: 100px; height: 28px; background: #0088ce;
      border-radius: 40px 0 0 0; margin-left: -2px;
    }
    .footer-contacts {
      display: flex; flex-wrap: wrap; gap: 8px 28px; align-items: center;
      justify-content: center; font-size: 12px; color: #334155;
    }
    .footer-contacts .item { display: inline-flex; align-items: center; gap: 6px; }
    .footer-contacts .icon {
      width: 18px; height: 18px; border-radius: 50%; border: 1.5px solid #0088ce;
      display: inline-flex; align-items: center; justify-content: center;
      color: #0088ce; font-size: 10px; flex-shrink: 0;
    }
    @media print {
      body { width: 100%; min-height: 100vh; }
      .page-header, .ref-date-block, .letter-content, .page-footer {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
    }
  </style>
</head>
<body>
  <div class="page-header">
    <div class="header-top-curve"></div>
    <div class="header-row">
      <div class="logo-wrap">
        ${
          logoUrl
            ? `<img src="${safe(
                logoUrl
              )}" alt="Logo" onerror="this.style.display='none';this.nextElementSibling.style.display='flex'" />
               <span class="logo-fallback" style="display:none">${
                 tenantName.charAt(0) || "S"
               }</span>`
            : `<span class="logo-fallback">${
                tenantName.charAt(0) || "S"
              }</span>`
        }
      </div>
      <div class="company-names">
        <div class="company-english">${tenantName}</div>
      </div>
    </div>
    <div class="header-line"></div>
    <div class="header-line-thin"></div>
  </div>

  <div class="ref-date-block">
    <div class="ref-date-inner">
      <div>
        <span class="label-am">ቁጥር</span> / Ref. No
        <span class="value">${refNo}</span>
      </div>
      <div>
        <span class="label-am">ቀን</span> / Date
        <span class="value">${letterDate}</span>
      </div>
    </div>
  </div>

  <div class="letter-content">
    <div class="letter-body">${body}</div>
  </div>

  <div class="page-footer">
    <div class="footer-line-row">
      <div class="footer-line"></div>
      <div class="footer-curve"></div>
    </div>
    <div class="footer-contacts">
      ${
        tenantPhone
          ? `<span class="item"><span class="icon">☎</span> ${tenantPhone}</span>`
          : ""
      }
      ${
        tenantEmail
          ? `<span class="item"><span class="icon">✉</span> ${tenantEmail}</span>`
          : ""
      }
      ${
        tenantAddress
          ? `<span class="item"><span class="icon">📍</span> ${tenantAddress}</span>`
          : ""
      }
    </div>
  </div>
</body>
</html>`;

    // Print via hidden iframe (avoids popup blockers)
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";
    iframe.style.opacity = "0";
    iframe.style.pointerEvents = "none";
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();

    // Wait for images/fonts, then print
    const doPrint = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } finally {
        setTimeout(() => {
          if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        }, 1000);
      }
    };

    // Give the iframe a moment to render (logo image, etc.)
    if (iframe.contentWindow.document.readyState === "complete") {
      setTimeout(doPrint, 250);
    } else {
      iframe.onload = () => setTimeout(doPrint, 250);
    }
  };

  return (
    <div className="min-h-full p-3 sm:p-4 md:p-8 font-sans bg-slate-50 text-slate-800 dark:bg-slate-950 dark:text-slate-100">
      <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
        {/* HEADER */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 p-4 sm:p-5 rounded-2xl shadow-sm border backdrop-blur-md bg-white border-slate-200 dark:bg-slate-900/40 dark:border-white/10 dark:shadow-xl">
          <div className="flex items-center gap-3">
            <div className="p-2.5 sm:p-3 rounded-xl shadow-inner border bg-gradient-to-br from-indigo-100 to-violet-50 border-indigo-200 text-indigo-600 dark:from-indigo-500/20 dark:to-violet-500/10 dark:border-indigo-500/30 dark:text-indigo-400">
              <FaFileAlt className="text-xl sm:text-2xl" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-black tracking-tight uppercase text-slate-900 dark:text-white">
                Letter Registry
              </h1>
              <p className="text-[11px] sm:text-xs mt-0.5 text-slate-500 dark:text-slate-400">
                <span className="font-mono font-semibold text-indigo-600 dark:text-indigo-400">
                  {filteredLetters.length}
                </span>{" "}
                matching ·{" "}
                <span className="font-mono font-semibold">
                  {letters.length}
                </span>{" "}
                total letters
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => openModal("create")}
              className="flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2.5 rounded-xl text-xs font-semibold transition-all shadow-sm bg-indigo-600 text-white hover:bg-indigo-700 dark:bg-indigo-600 dark:hover:bg-indigo-500"
            >
              <FaPlus />
              New Letter
            </button>
          </div>
        </div>

        {/* SEARCH + FILTERS */}
        <div className="p-3 sm:p-4 rounded-xl border backdrop-blur-sm bg-white border-slate-200 dark:bg-slate-900/20 dark:border-white/5 space-y-3">
          <div className="relative">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400 dark:text-slate-500">
              <FaSearch className="text-xs" />
            </span>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="Search by code, title, body, approver..."
              className="w-full rounded-xl pl-9 pr-4 py-2.5 text-xs transition focus:outline-none focus:border-indigo-500/40 bg-slate-50 border border-slate-200 text-slate-800 placeholder-slate-400 dark:bg-slate-950/60 dark:border-white/10 dark:text-white dark:placeholder-slate-600"
            />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {[
              {
                label: "Status",
                value: statusFilter,
                setter: setStatusFilter,
                options: filterOptions.statuses,
              },
            ].map((filter) => (
              <select
                key={filter.label}
                value={filter.value}
                onChange={(e) => filter.setter(e.target.value)}
                aria-label={`Filter by ${filter.label}`}
                className="w-full rounded-xl px-3 py-2.5 text-xs border focus:outline-none focus:border-indigo-500/40 bg-slate-50 border-slate-200 text-slate-700 dark:bg-slate-950/60 dark:border-white/10 dark:text-slate-200"
              >
                <option value="">All {filter.label}s</option>
                {filter.options.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            ))}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-500 dark:text-slate-400">
            <span>
              {filteredLetters.length} matching records ({letters.length} total)
            </span>
            <button
              type="button"
              onClick={clearFilters}
              className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 dark:bg-slate-800 dark:border-white/10 dark:text-slate-300 dark:hover:bg-slate-700"
            >
              Clear filters
            </button>
          </div>
        </div>

        {/* ERROR */}
        {error && (
          <div className="p-3 sm:p-4 rounded-xl border text-xs font-medium bg-red-50 border-red-200 text-red-600 dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400">
            {error}
          </div>
        )}

        {/* ========== MOBILE CARD LIST ========== */}
        <div className="md:hidden space-y-3">
          {isLoading ? (
            <div className="flex items-center justify-center gap-3 py-16 text-slate-500 italic text-sm">
              <FaSpinner className="animate-spin text-indigo-500" />
              Loading letters...
            </div>
          ) : filteredLetters.length === 0 ? (
            <div className="text-center py-12 text-slate-500 italic text-sm">
              No letters match the selected filters.
            </div>
          ) : (
            filteredLetters.map((letter, index) => (
              <div
                key={letter.id ?? `letter-${index}`}
                className="rounded-2xl border bg-white border-slate-200 dark:bg-slate-900/40 dark:border-white/10 p-4 space-y-3 shadow-sm"
              >
                <div className="flex justify-between items-start gap-2">
                  <div className="min-w-0">
                    <div className="text-[10px] text-slate-400 font-mono mb-1">
                      SN: {index + 1}
                    </div>
                    <div className="flex items-center gap-1.5 font-bold text-slate-900 dark:text-white text-sm">
                      <FaFileAlt className="opacity-40 text-[11px] shrink-0" />
                      <span className="truncate">
                        {letter.letterCode || "PENDING"}
                      </span>
                    </div>
                    <p className="text-xs text-slate-600 dark:text-slate-300 mt-1 line-clamp-2 font-medium">
                      {letter.title}
                    </p>
                  </div>
                  <span
                    className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold inline-flex items-center gap-1 border ${
                      String(letter.status || "").toUpperCase() === "APPROVED"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/10"
                        : "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/10"
                    }`}
                  >
                    {String(letter.status || "").toUpperCase() ===
                    "APPROVED" ? (
                      <FaCheckCircle className="text-[9px]" />
                    ) : (
                      <FaClock className="text-[9px]" />
                    )}
                    {letter.status || "DRAFT"}
                  </span>
                </div>

                <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500 dark:text-slate-400">
                  {letter.approverName && (
                    <span className="flex items-center gap-1">
                      <FaUser className="text-[9px]" />
                      {letter.approverName}
                    </span>
                  )}
                  <span className="font-mono">
                    {letter.date
                      ? new Date(letter.date).toLocaleDateString(undefined, {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })
                      : "—"}
                  </span>
                </div>

                <div className="flex justify-between items-center pt-2 border-t border-slate-100 dark:border-white/5">
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 line-clamp-1 max-w-[60%]">
                    {(letter.body || "").slice(0, 80)}
                    {(letter.body || "").length > 80 ? "…" : ""}
                  </p>

                  <div className="flex gap-1.5">
                    <button
                      type="button"
                      onClick={() => openModal("view", letter)}
                      className="p-2 rounded-lg border bg-slate-50 border-slate-200 text-slate-500 dark:bg-white/5 dark:border-white/10 dark:text-slate-300"
                    >
                      <FaEye className="text-xs" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePrint(letter)}
                      className="p-2 rounded-lg border bg-slate-50 border-slate-200 text-slate-500 dark:bg-white/5 dark:border-white/10 dark:text-slate-300"
                      title="Print"
                    >
                      <FaPrint className="text-xs" />
                    </button>
                    {String(letter.status || "").toUpperCase() !==
                      "APPROVED" && (
                      <>
                        <button
                          type="button"
                          onClick={() => handleApprove(letter.id)}
                          className="p-2 rounded-lg border bg-emerald-50 border-emerald-200 text-emerald-600 dark:bg-emerald-500/10 dark:border-emerald-500/20 dark:text-emerald-400"
                        >
                          <FaCheck className="text-xs" />
                        </button>
                        <button
                          type="button"
                          onClick={() => openModal("edit", letter)}
                          className="p-2 rounded-lg border bg-blue-50 border-blue-200 text-blue-600 dark:bg-blue-500/10 dark:border-blue-500/10 dark:text-blue-400"
                        >
                          <FaEdit className="text-xs" />
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            handleDelete(letter.id, letter.letterCode)
                          }
                          className="p-2 rounded-lg border bg-red-50 border-red-200 text-red-600 dark:bg-red-500/10 dark:border-red-500/10 dark:text-red-400"
                        >
                          <FaTrash className="text-xs" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* ========== DESKTOP TABLE ========== */}
        <div className="hidden md:block border rounded-2xl shadow-sm backdrop-blur-md bg-white border-slate-200 dark:bg-slate-900/30 dark:border-white/10 dark:shadow-2xl">
          <div className="overflow-x-auto rounded-2xl">
            <table className="w-full text-left border-collapse min-w-[900px]">
              <thead>
                <tr className="border-b text-[10px] font-bold tracking-wider uppercase bg-slate-50 border-slate-200 text-slate-500 dark:bg-slate-950/50 dark:border-white/10 dark:text-slate-400">
                  <th className="px-3 py-4 text-center">SN</th>
                  <SortableHeader
                    label="Letter Code"
                    sortKey="letterCode"
                    className="px-4 lg:px-5"
                  />
                  <SortableHeader label="Title" sortKey="title" />
                  <SortableHeader label="Approver" sortKey="approverName" />
                  <SortableHeader label="Date" sortKey="date" />
                  <SortableHeader
                    label="Status"
                    sortKey="status"
                    className="text-center"
                  />
                  <th className="px-4 lg:px-5 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y font-mono text-xs divide-slate-100 text-slate-600 dark:divide-white/5 dark:text-slate-300">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="text-center py-12 text-slate-500 font-sans italic dark:text-slate-500"
                    >
                      <div className="flex items-center justify-center gap-3">
                        <FaSpinner className="animate-spin text-indigo-500 text-base" />
                        <span>Loading letters...</span>
                      </div>
                    </td>
                  </tr>
                ) : filteredLetters.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="text-center py-12 text-slate-500 font-sans italic dark:text-slate-500"
                    >
                      No letters match the selected filters.
                    </td>
                  </tr>
                ) : (
                  filteredLetters.map((letter, index) => (
                    <tr
                      key={letter.id ?? `letter-${index}`}
                      className="group transition hover:bg-slate-50 dark:hover:bg-white/[0.02]"
                    >
                      <td className="px-3 py-3.5 text-center text-slate-400 font-mono">
                        {index + 1}
                      </td>
                      <td className="px-4 lg:px-5 py-3.5 font-sans font-bold tracking-wide text-slate-900 dark:text-white">
                        <div className="flex items-center gap-1.5">
                          <FaFileAlt className="opacity-40 text-slate-400 text-[11px]" />
                          {letter.letterCode || "PENDING"}
                        </div>
                      </td>
                      <td className="px-3 lg:px-4 py-3.5">
                        <div className="text-slate-800 dark:text-slate-200 font-medium max-w-[220px] lg:max-w-sm truncate">
                          {letter.title}
                        </div>
                        <div className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5 line-clamp-1 max-w-[220px] lg:max-w-sm">
                          {(letter.body || "").slice(0, 60)}
                          {(letter.body || "").length > 60 ? "…" : ""}
                        </div>
                      </td>

                      <td className="px-3 lg:px-4 py-3.5">
                        {letter.approverName ? (
                          <span className="text-slate-700 dark:text-slate-300 flex items-center gap-1 text-[11px]">
                            <FaUser className="text-[9px] opacity-50" />
                            {letter.approverName}
                          </span>
                        ) : (
                          <span className="text-slate-400 dark:text-slate-600 text-[11px]">
                            —
                          </span>
                        )}
                      </td>
                      <td className="px-3 lg:px-4 py-3.5 text-slate-500 dark:text-slate-400 font-mono text-[11px]">
                        {letter.date
                          ? new Date(letter.date).toLocaleDateString(
                              undefined,
                              {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              }
                            )
                          : "—"}
                      </td>
                      <td className="px-3 lg:px-4 py-3.5 text-center">
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide inline-flex items-center gap-1 border ${
                            String(letter.status || "").toUpperCase() ===
                            "APPROVED"
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/10"
                              : "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/10"
                          }`}
                        >
                          {String(letter.status || "").toUpperCase() ===
                          "APPROVED" ? (
                            <FaCheckCircle className="text-[9px]" />
                          ) : (
                            <FaClock className="text-[9px]" />
                          )}
                          {letter.status || "DRAFT"}
                        </span>
                      </td>
                      <td className="px-4 lg:px-5 py-3.5">
                        <div className="flex justify-end gap-1.5 lg:gap-2">
                          <button
                            type="button"
                            onClick={() => openModal("view", letter)}
                            title="View Letter"
                            className="p-2 rounded-lg border transition bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-800 dark:bg-white/5 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/10 dark:hover:text-white"
                          >
                            <FaEye className="text-xs" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handlePrint(letter)}
                            title="Print Letter"
                            className="p-2 rounded-lg border transition bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-800 dark:bg-white/5 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/10 dark:hover:text-white"
                          >
                            <FaPrint className="text-xs" />
                          </button>
                          {String(letter.status || "").toUpperCase() !==
                            "APPROVED" && (
                            <>
                              <button
                                type="button"
                                onClick={() => handleApprove(letter.id)}
                                title="Approve Letter"
                                className="p-2 rounded-lg border transition bg-emerald-50 border-emerald-200 text-emerald-600 hover:bg-emerald-600 hover:text-white dark:bg-emerald-500/10 dark:border-emerald-500/20 dark:text-emerald-400 dark:hover:bg-emerald-600 dark:hover:text-white"
                              >
                                <FaCheck className="text-xs" />
                              </button>
                              <button
                                type="button"
                                onClick={() => openModal("edit", letter)}
                                title="Edit Letter"
                                className="p-2 rounded-lg border transition bg-blue-50 border-blue-200 text-blue-600 hover:bg-blue-100 dark:bg-blue-500/10 dark:border-blue-500/10 dark:text-blue-400 dark:hover:bg-blue-500/20"
                              >
                                <FaEdit className="text-xs" />
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  handleDelete(letter.id, letter.letterCode)
                                }
                                title="Delete Letter"
                                className="p-2 rounded-lg border transition bg-red-50 border-red-200 text-red-600 hover:bg-red-100 dark:bg-red-500/10 dark:border-red-500/10 dark:text-red-400 dark:hover:bg-red-500/20"
                              >
                                <FaTrash className="text-xs" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ========== MODAL ========== */}
        <div
          className={`fixed inset-0 z-[2000] flex flex-col justify-end transition-opacity duration-300 ${
            isModalOpen
              ? "opacity-100 pointer-events-auto"
              : "opacity-0 pointer-events-none"
          }`}
        >
          <div
            className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
            onClick={() => setIsModalOpen(false)}
          />

          <div
            className={`relative w-full max-w-2xl mx-auto rounded-t-3xl p-4 sm:p-6 max-h-[92vh] overflow-y-auto shadow-2xl border bg-white border-slate-200 dark:bg-slate-900 dark:border-white/10 transition-transform duration-300 transform ${
              isModalOpen ? "translate-y-0" : "translate-y-full"
            }`}
          >
            <div className="flex justify-between items-center pb-3 mb-4 sm:mb-5 border-b border-slate-200 dark:border-white/5">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-lg bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400">
                  <FaFileAlt className="text-xs" />
                </div>
                <h3 className="text-sm font-bold capitalize text-slate-900 dark:text-white">
                  {modalMode} Letter
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="p-2 rounded-lg transition text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:text-white dark:hover:bg-white/5"
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleSave} className="space-y-5">
              {error && (
                <div className="p-3 rounded-xl border text-xs font-medium bg-red-50 border-red-200 text-red-600 dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400">
                  {error}
                </div>
              )}

              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500 dark:text-slate-400">
                  Letter Code
                </label>
                <input
                  type="text"
                  placeholder="Generated automatically"
                  disabled
                  value={formData.letterCode}
                  className="w-full rounded-xl px-3 sm:px-4 py-2.5 text-xs font-mono bg-slate-100 border border-slate-200 text-slate-400 dark:bg-slate-950/40 dark:border-white/5 dark:text-slate-500"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500 dark:text-slate-400">
                  Title
                </label>
                <input
                  type="text"
                  placeholder="Letter subject / title"
                  disabled={modalMode === "view"}
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  className="w-full rounded-xl px-3 sm:px-4 py-2.5 text-xs focus:outline-none focus:border-indigo-500/40 bg-slate-50 border border-slate-200 text-slate-800 placeholder-slate-400 dark:bg-slate-950 dark:border-white/10 dark:text-white"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] uppercase font-bold tracking-wider flex items-center gap-1 text-slate-500 dark:text-slate-400">
                    <FaCalendarAlt className="text-[10px]" />
                    Letter Date
                  </label>
                  <input
                    type="date"
                    required
                    disabled={modalMode === "view"}
                    value={formData.date}
                    onChange={(e) =>
                      setFormData({ ...formData, date: e.target.value })
                    }
                    className="w-full rounded-xl px-3 sm:px-4 py-2.5 text-xs font-mono focus:outline-none focus:border-indigo-500/40 bg-slate-50 border border-slate-200 text-slate-800 dark:bg-slate-950 dark:border-white/10 dark:text-white"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500 dark:text-slate-400">
                  Body
                </label>

                {modalMode === "view" ? (
                  <div
                    className="quill-content w-full min-h-[200px] rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-950 p-3 text-sm text-slate-800 dark:text-slate-200"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(formData.body || ""),
                    }}
                  />
                ) : (
                  <div className="letter-editor rounded-xl overflow-hidden border border-slate-200 dark:border-white/10">
                    <ReactQuill
                      theme="snow"
                      value={formData.body}
                      onChange={(body) =>
                        setFormData((prev) => ({ ...prev, body }))
                      }
                      readOnly={modalMode === "view"}
                      placeholder="Write your letter content here..."
                      modules={{
                        toolbar: [
                          [{ header: [1, 2, 3, false] }],
                          ["bold", "italic", "underline", "strike"],
                          [{ color: [] }, { background: [] }],
                          [{ list: "ordered" }, { list: "bullet" }],
                          [{ align: [] }],
                          [{ indent: "-1" }, { indent: "+1" }],
                          ["blockquote", "link"],
                          ["clean"],
                        ],
                      }}
                      className="bg-white dark:bg-slate-950 text-slate-800 dark:text-slate-200"
                    />
                  </div>
                )}
              </div>

              {(modalMode === "view" || modalMode === "edit") &&
                formData.approverName && (
                  <div className="rounded-xl border border-slate-200 dark:border-white/10 p-3 bg-slate-50/50 dark:bg-slate-950/30">
                    <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500 dark:text-slate-400 mb-1">
                      Approver
                    </div>
                    <div className="text-xs text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                      <FaUser className="text-[10px] opacity-60" />
                      {formData.approverName}
                    </div>
                  </div>
                )}

              <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="w-full sm:w-auto px-4 py-2.5 rounded-xl text-xs font-semibold bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10"
                >
                  Cancel
                </button>
                {modalMode === "view" && selectedLetter && (
                  <button
                    type="button"
                    onClick={() => handlePrint(selectedLetter)}
                    className="w-full sm:w-auto px-4 py-2.5 rounded-xl text-xs font-semibold border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:bg-slate-800 dark:border-white/10 dark:text-slate-200"
                  >
                    <span className="inline-flex items-center gap-1.5">
                      <FaPrint className="text-[11px]" /> Print
                    </span>
                  </button>
                )}
                {modalMode !== "view" && (
                  <button
                    type="submit"
                    className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 dark:bg-indigo-600 dark:hover:bg-indigo-500 text-xs font-semibold transition"
                  >
                    {modalMode === "create" ? "Create Letter" : "Save Changes"}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LetterManagement;
